import axios, { AxiosRequestConfig } from 'axios';
import qs from 'qs';

export interface PlannerApiFetcher {
  get(endpoint: string, options?: AxiosRequestConfig): Promise<any>;

  post(endpoint: string, dataToPost: any, options?: AxiosRequestConfig): Promise<any>;

  put(endpoint: string, dataToPost: any, options?: AxiosRequestConfig): Promise<any>;

  delete(endpoint: string, id?: number | string, options?: AxiosRequestConfig): void;

  deleteList(endpoint: string, ids: number[] | string, options?: AxiosRequestConfig): void;

  patch(endpoint: string, data: any, options?: AxiosRequestConfig): Promise<any>;

  postFile(endpoint: string, data: any, options?: AxiosRequestConfig): Promise<any>;

  token: string;

  prepareParams: (params: any) => string;
}

interface PlannerApiFetcherConfig {
  apiUrl: string;
}

export class PlannerApiFetcherImpl implements PlannerApiFetcher {
  get token(): string {
    return this._token;
  }

  set token(value: string) {
    this._token = value;
  }
  constructor(private config: PlannerApiFetcherConfig) {
    axios.defaults.paramsSerializer = this.prepareParams;
    axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const errorMessage =
          error && error.response && error.response.data && error.response.data.Message
            ? error.response.data.Message
            : error;

        return Promise.reject({ error, errorMessage });
      },
    );
  }

  private _token: string;

  prepareParams = (params: any) => qs.stringify(params, { allowDots: true });

  private combineUrl(endpoint: string): string {
    return `${this.config.apiUrl}${endpoint}`;
  }

  private getOptions(options?: AxiosRequestConfig): AxiosRequestConfig {
    let additionnalHeaders: { Authorization?: string } = {};
    if (this._token) {
      additionnalHeaders = {
        Authorization: `Bearer ${this._token}`,
      };
    }
    return {
      withCredentials: false,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        ...additionnalHeaders,
      },
      ...options,
    };
  }

  public async get(endpoint: string, options?: AxiosRequestConfig) {
    try {
      const finalOptions = this.getOptions(options);
      const response = await axios.get(this.combineUrl(endpoint), finalOptions);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async post(endpoint: string, dataToPost: any, options?: AxiosRequestConfig) {
    try {
      const finalOptions = this.getOptions(options);
      const response = await axios.post(this.combineUrl(endpoint), dataToPost, finalOptions);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async put(endpoint: string, dataToPost: any, options?: AxiosRequestConfig) {
    try {
      const finalOptions = this.getOptions(options);

      const response = await axios.put(this.combineUrl(endpoint), dataToPost, finalOptions);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async delete(endpoint: string, id?: number | string, options?: AxiosRequestConfig) {
    try {
      const finalOptions = this.getOptions(options);

      let url = this.combineUrl(endpoint);
      if (id) {
        url += '/' + id;
      }

      const response = await axios.delete(url, finalOptions);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async deleteList(endpoint: string, ids: number[] | string, options?: AxiosRequestConfig) {
    try {
      const finalOptions = this.getOptions({
        data: ids,
        ...options,
      });

      const response = await axios.delete(this.combineUrl(endpoint), finalOptions);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async patch(endpoint: string, data: any, options?: AxiosRequestConfig): Promise<any> {
    try {
      const finalOptions = this.getOptions(options);

      const response = await axios.patch(this.combineUrl(endpoint), data, finalOptions);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async postFile(endpoint: string, data: any, options?: AxiosRequestConfig) {
    try {
      const finalOptions = this.getOptions(options);

      const response = await axios.post(this.combineUrl(endpoint), data, finalOptions);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}
